import type { OrderAttribute } from "@jti/magento";
import { IOrderInputTypeEnum } from "@jti/magento";

import { checkboxFormValueAdapter } from "@checkout-v2/features/OrderAttributes/utils/checkboxFormValueAdapter";
import { coerceAttributeBoolean } from "@checkout-v2/features/OrderAttributes/utils/coerceAttributeBoolean/coerceAttributeBoolean";

import type { LastOrderAttribute } from ".";

/**
 * Builds Fast Flow pre-fill defaults by matching each defined order attribute against the
 * customer's last order values. Required attributes are skipped: the market mandates they be
 * re-entered manually on every order, so they are never pre-populated.
 */
export const getOrderAttributesDefaultValues = (
  definedAttributes: OrderAttribute[] | undefined,
  lastOrderAttributes: LastOrderAttribute[] | undefined
): Record<string, string | boolean> => {
  if (!definedAttributes || definedAttributes.length === 0) return {};
  const map = new Map<string, string>();
  (lastOrderAttributes || []).forEach((a) => {
    map.set(a.attribute_code, a.value);
  });

  const defaults: Record<string, string | boolean> = {};

  for (const attr of definedAttributes) {
    if (attr.is_required) continue;

    const lastValue = map.get(attr.code);
    if (!lastValue) continue;

    switch (attr.input_type) {
      case IOrderInputTypeEnum.Checkbox: {
        const boolVal = coerceAttributeBoolean(lastValue);
        defaults[attr.code] = checkboxFormValueAdapter(boolVal, attr.data_type);
        break;
      }
      case IOrderInputTypeEnum.Text:
      default: {
        defaults[attr.code] = lastValue;
        break;
      }
    }
  }

  return defaults;
};
